import { Box, Typography, Paper, Alert, Chip } from '@mui/material';
import { CheckCircle, Link2, Unlink } from 'lucide-react';
import { ImportType, RELATIONSHIP_CONFIGS } from '../BulkImport';

interface ConfirmationStepProps {
  importedCount: number;
  importType: ImportType;
  relationshipStats: Record<string, { matched: number; unmatched: number }>;
}

export const ConfirmationStep = ({ importedCount, importType, relationshipStats }: ConfirmationStepProps) => {
  const relationshipConfigs = RELATIONSHIP_CONFIGS[importType] || [];

  const getImportTypeLabel = () => {
    switch (importType) {
      case 'students':
        return 'students';
      case 'parents':
        return 'parents';
      case 'community':
        return 'community members';
      case 'fees':
        return 'fees';
      case 'courses':
        return 'courses';
    }
  };

  const hasRelationshipStats = Object.values(relationshipStats).some(
    stat => stat.matched > 0 || stat.unmatched > 0
  );

  const totalMatched = Object.values(relationshipStats).reduce((sum, stat) => sum + stat.matched, 0);
  const totalUnmatched = Object.values(relationshipStats).reduce((sum, stat) => sum + stat.unmatched, 0);

  return (
    <Box>
      <Paper
        sx={{
          p: 6,
          textAlign: 'center',
          bgcolor: 'success.light',
          color: 'success.dark'
        }}
      >
        <CheckCircle size={64} style={{ marginBottom: 16 }} />
        <Typography variant="h4" gutterBottom>
          Import Successful!
        </Typography>
        <Typography variant="h6" gutterBottom>
          {importedCount} {getImportTypeLabel()} imported successfully
        </Typography>
        {hasRelationshipStats && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Chip
              icon={<Link2 size={16} />}
              label={`${totalMatched} relationships linked`}
              color="primary"
              sx={{ bgcolor: 'white' }}
            />
            {totalUnmatched > 0 && (
              <Chip
                icon={<Unlink size={16} />}
                label={`${totalUnmatched} relationships not found`}
                color="warning"
                sx={{ bgcolor: 'white' }}
              />
            )}
          </Box>
        )}
      </Paper>

      <Alert severity="success" sx={{ mt: 3 }}>
        The data has been successfully imported into the system. You can now view and manage the imported records in their respective management pages.
      </Alert>

      {hasRelationshipStats && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Link2 size={20} /> Relationship Summary
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {relationshipConfigs.map(config => {
              const stats = relationshipStats[config.id];
              if (!stats || (stats.matched === 0 && stats.unmatched === 0)) return null;

              return (
                <Box
                  key={config.id}
                  sx={{
                    p: 2,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2">{config.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {config.description}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Chip
                      icon={<Link2 size={14} />}
                      label={`${stats.matched} matched`}
                      size="small"
                      color="success"
                    />
                    {stats.unmatched > 0 && (
                      <Chip
                        icon={<Unlink size={14} />}
                        label={`${stats.unmatched} not found`}
                        size="small"
                        color="warning"
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
          {totalUnmatched > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Some relationships could not be matched. You can manually link these records in the management pages.
            </Alert>
          )}
        </Paper>
      )}

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Next Steps
        </Typography>
        <Typography variant="body2" component="div">
          <ul>
            <li>Review the imported records in the {getImportTypeLabel()} management page</li>
            <li>Verify that all data was imported correctly</li>
            <li>Update any additional information as needed</li>
            {importType === 'students' && !hasRelationshipStats && (
              <>
                <li>Assign students to their respective courses</li>
                <li>Configure pre-assessed fees if applicable</li>
                <li>Link students to their parents</li>
              </>
            )}
            {importType === 'parents' && !hasRelationshipStats && (
              <li>Link parents to their students</li>
            )}
            {totalUnmatched > 0 && (
              <li>Review and manually link the {totalUnmatched} unmatched relationship{totalUnmatched !== 1 ? 's' : ''}</li>
            )}
          </ul>
        </Typography>
      </Paper>
    </Box>
  );
};
